
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { MasterUser } from '@/types/userRole';
import { useToast } from '@/hooks/use-toast';

interface AssignRoleDialogProps {
  open: boolean;
  user: MasterUser;
  onClose: () => void;
  onRoleUpdate?: (userId: string, newRole: string) => Promise<void>;
}

const AssignRoleDialog: React.FC<AssignRoleDialogProps> = ({ open, user, onClose, onRoleUpdate }) => {
  const [selectedRole, setSelectedRole] = useState<string>(user.role || 'user');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Update the selected role when the user prop changes
  useEffect(() => {
    if (user) {
      setSelectedRole(user.role || 'user');
    }
  }, [user]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // If onRoleUpdate is provided, use it (allows parent component to handle the update)
      if (onRoleUpdate) {
        await onRoleUpdate(user.id, selectedRole);
      } else {
        // Fallback to the mock implementation
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      toast({
        title: "Role Updated",
        description: `${user.name}'s role has been updated to ${selectedRole}`,
      });
      onClose();
    } catch (error) {
      console.error('Error updating role:', error);
      toast({
        title: "Error",
        description: "Failed to update user role",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Role to {user.name}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <RadioGroup value={selectedRole} onValueChange={setSelectedRole}>
            <div className="flex items-center space-x-2 mb-3">
              <RadioGroupItem value="superadmin" id="superadmin" />
              <Label htmlFor="superadmin" className="font-medium">Super Admin</Label>
            </div>
            <div className="flex items-center space-x-2 mb-3">
              <RadioGroupItem value="reseller" id="reseller" />
              <Label htmlFor="reseller" className="font-medium">Reseller</Label>
            </div>
            <div className="flex items-center space-x-2 mb-3">
              <RadioGroupItem value="admin" id="admin" />
              <Label htmlFor="admin" className="font-medium">Admin</Label>
            </div>
            <div className="flex items-center space-x-2 mb-3">
              <RadioGroupItem value="manager" id="manager" />
              <Label htmlFor="manager" className="font-medium">Manager</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="user" id="user" />
              <Label htmlFor="user" className="font-medium">User</Label>
            </div>
          </RadioGroup>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Updating...' : 'Update Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignRoleDialog;
